/**
 * ConstructionDataTable Component
 * 
 * Table component for displaying construction worker payslip data.
 * Displays data in a formatted table with columns for:
 * - Year, Month
 * - Site (現場名/genbaName), Employee name (従業員名)
 * - Attendance days (出勤日数)
 * - Base salary (基本給), Total wage (賃金総額)
 * - Insurance deductions (健康保険, 雇用保険, 厚生年金)
 * - Income tax (所得税)
 * - Total deductions (控除合計)
 * - Transfer amount (振込金額)
 * - Delete operation per row
 * 
 * Features:
 * - Currency formatting with Japanese locale
 * - Individual record deletion with confirmation
 * - Empty state handling
 * - Loading state for delete operations
 * - Site and employee name display (unlike office employees)
 * 
 * Note: Construction workers use site names and employee names instead of
 * employee codes and departments like office employees.
 * 
 * @component
 */

import React from 'react';
import { ConstructionBonusDataRow } from '../../../../utils/constructionBonusContract';

/**
 * Interface defining the structure of construction worker salary data.
 */
interface ConstructionSalaryData {
  genbaName: string;                          // Site name (現場名)
  employeeName: string;                       // Employee name (従業員名)
  year: number;                               // Year
  month: number;                              // Month
  attendanceDays: number;                     // Attendance days (出勤日数)
  totalWage: number;                          // Total wage (賃金総額)
  healthInsurance: number;                    // Health insurance (健康保険)
  employmentInsurance: number;                // Employment insurance (雇用保険)
  pensionInsurance: number;                   // Pension insurance (厚生年金)
  incomeTax: number;                          // Income tax (所得税)
  fixedReduction: number;                     // Fixed reduction (定額減税)
  residentTax: number;                        // Resident tax (住民税)
  legalDeductionTotal: number;                // Legal deduction total (法定控除合計)
  deductedPayment1: number;                   // Deducted payment 1 (差引支給額1)
  mealCost: number;                           // Meal cost (食事代)
  miscellaneousExpenses: number;              // Miscellaneous expenses (雑費)
  gasolineCost: number;                       // Gasoline cost (ガソリン代)
  advancePayment: number;                     // Advance payment (前渡金)
  beddingCost: number;                        // Bedding cost (寝具代)
  leaseCost: number;                          // Lease cost (リース代)
  agreementDeductionTotal: number;            // Agreement deduction total (協定控除合計)
  totalDeductions: number;                    // Total deductions (控除合計)
  deductedPayment2: number;                   // Deducted payment 2 (差引支給額2)
  otherPayment: number;                      // その他支給
  otherDeduction: number;                    // その他控除
  yearEndRefund: number;                     // 年末調整還付金
  transferAmount: number;                     // Transfer amount (振込金額)
  otherPaymentBreakdown?: string;             // その他支給内訳
  otherDeductionBreakdown?: string;           // その他控除内訳
  remarks?: string;                          // 備考
  paidLeaveDays: number;                      // Paid leave days (有給日数)
  remainingPaidLeave: number;                 // Remaining paid leave (残有給)
  overtimeHours: number;                      // Overtime hours (残業時間)
  baseSalary: number;                         // Base salary (基本給)
  pieceRatePay: number;                       // Piece rate pay (出来高給)
  siteAllowance: number;                      // Site allowance (現場手当)
  overtimeAllowance: number;                  // Overtime allowance (残業手当)
  nightShiftAllowance: number;                // Night shift allowance (夜勤手当)
  holidayAllowance: number;                   // Holiday allowance (休日手当)
  otherAllowance?: number;                    // Other allowance (その他手当)
  incomeTax2: number;                         // Income tax 2 (所得税2)
  // Daily attendance data (day1 to day31)
  day1: number;
  day2: number;
  day3: number;
  day4: number;
  day5: number;
  day6: number;
  day7: number;
  day8: number;
  day9: number;
  day10: number;
  day11: number;
  day12: number;
  day13: number;
  day14: number;
  day15: number;
  day16: number;
  day17: number;
  day18: number;
  day19: number;
  day20: number;
  day21: number;
  day22: number;
  day23: number;
  day24: number;
  day25: number;
  day26: number;
  day27: number;
  day28: number;
  day29: number;
  day30: number;
  day31: number;
}

/**
 * Props interface for ConstructionDataTable component.
 */
interface ConstructionDataTableProps {
  filteredData: Array<ConstructionSalaryData | ConstructionBonusDataRow>;
  deleteLoading: boolean;
  dataType?: 'salary' | 'bonus';
  onDeleteIndividualRecord: (
    genbaName: string,
    employeeName: string,
    year: number,
    monthOrPeriod: number | string
  ) => void;
}

/**
 * ConstructionDataTable Component
 * 
 * Renders a table displaying construction worker payslip records.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const ConstructionDataTable: React.FC<ConstructionDataTableProps> = ({
  filteredData,
  deleteLoading,
  dataType = 'salary',
  onDeleteIndividualRecord
}) => {
  /**
   * Formats a number as Japanese currency format.
   * 
   * Uses Intl.NumberFormat with 'ja-JP' locale to format numbers
   * with appropriate thousands separators.
   * 
   * @param amount - Number to format
   * @returns Formatted string (e.g., "1,234,567")
   * 
   * @example
   * ```typescript
   * formatCurrency(1234567); // Returns: "1,234,567"
   * ```
   */
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP').format(amount);
  };

  /**
   * Empty state - shows message when no data is available.
   */
  if (filteredData.length === 0) {
    return (
      <div className="data-table-container">
        <div className="no-data">
          <p>表示するデータがありません</p>
        </div>
      </div>
    );
  }

  if (dataType === 'bonus') {
    const bonusRows = filteredData as ConstructionBonusDataRow[];
    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>年</th>
              <th>期</th>
              <th>現場名</th>
              <th>従業員名</th>
              <th>賞与</th>
              <th>支給合計</th>
              <th>健康保険</th>
              <th>雇用保険</th>
              <th>厚生年金</th>
              <th>所得税</th>
              <th>控除合計</th>
              <th>差引支給額</th>
              <th>振込金額</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {bonusRows.map((data, index) => (
              <tr key={`${data.genbaName}-${data.employeeName}-${data.year}-${data.bonusPeriod}-${index}`}>
                <td>{data.year}</td>
                <td>{data.bonusPeriod}月</td>
                <td>{data.genbaName}</td>
                <td>{data.employeeName}</td>
                <td>¥{formatCurrency(data.bonus || 0)}</td>
                <td>¥{formatCurrency(data.totalPayment || 0)}</td>
                <td>¥{formatCurrency(data.healthInsurance || 0)}</td>
                <td>¥{formatCurrency(data.employmentInsurance || 0)}</td>
                <td>¥{formatCurrency(data.pensionInsurance || 0)}</td>
                <td>¥{formatCurrency(data.incomeTax || 0)}</td>
                <td>¥{formatCurrency(data.deductionTotal || 0)}</td>
                <td className="net-salary">¥{formatCurrency(data.netPaymentAmount || 0)}</td>
                <td>¥{formatCurrency(data.transferAmount || 0)}</td>
                <td>
                  <button
                    className="delete-row-btn"
                    onClick={() => {
                      if (window.confirm('本当に削除してもよろしいですか？')) {
                        onDeleteIndividualRecord(
                          data.genbaName,
                          data.employeeName,
                          data.year,
                          data.bonusPeriod
                        );
                      }
                    }}
                    disabled={deleteLoading}
                    title="このレコードを削除"
                  >
                    {deleteLoading ? '削除中...' : '削除'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const salaryRows = filteredData as ConstructionSalaryData[];
  return (
    <div className="data-table-container">
      <table className="data-table">
        {/* Table Header */}
        <thead>
          <tr>
            <th>年</th>
            <th>月</th>
            <th>現場名</th> {/* Site name (unique to construction workers) */}
            <th>従業員名</th> {/* Employee name (uses name instead of code) */}
            <th>出勤日数</th> {/* Attendance days */}
            <th>基本給</th> {/* Base salary */}
            <th>賃金総額</th> {/* Total wage */}
            <th>健康保険</th> {/* Health insurance */}
            <th>雇用保険</th> {/* Employment insurance */}
            <th>厚生年金</th> {/* Pension insurance */}
            <th>所得税</th> {/* Income tax */}
            <th>控除合計</th> {/* Total deductions */}
            <th>振込金額</th> {/* Transfer amount (net payment) */}
            <th>操作</th> {/* Actions column */}
          </tr>
        </thead>
        <tbody>
          {/* Map through filtered data and render rows */}
          {salaryRows.map((data, index) => (
            <tr key={`${data.genbaName}-${data.employeeName}-${data.year}-${data.month}-${index}`}>
              <td>{data.year}</td>
              <td>{data.month}</td>
              <td>{data.genbaName}</td>
              <td>{data.employeeName}</td>
              {/* Attendance days with fallback to 0, formatted with 日 suffix */}
              <td>{data.attendanceDays || 0}日</td>
              {/* Currency-formatted amounts with fallback to 0 */}
              <td>¥{formatCurrency(data.baseSalary || 0)}</td>
              <td>¥{formatCurrency(data.totalWage || 0)}</td>
              <td>¥{formatCurrency(data.healthInsurance || 0)}</td>
              <td>¥{formatCurrency(data.employmentInsurance || 0)}</td>
              <td>¥{formatCurrency(data.pensionInsurance || 0)}</td>
              <td>¥{formatCurrency(data.incomeTax || 0)}</td>
              <td>¥{formatCurrency(data.totalDeductions || 0)}</td>
              {/* Transfer amount (final payment) - highlighted with net-salary class */}
              <td className="net-salary">¥{formatCurrency(data.transferAmount || 0)}</td>
              <td>
                {/* Delete button with confirmation dialog */}
                <button
                  className="delete-row-btn"
                  onClick={() => { 
                    if (window.confirm('本当に削除してもよろしいですか？')) { 
                      onDeleteIndividualRecord(
                        data.genbaName, 
                        data.employeeName, 
                        data.year, 
                        data.month
                      ); 
                    } 
                  }}
                  disabled={deleteLoading}
                  title="このレコードを削除"
                >
                  {deleteLoading ? '削除中...' : '削除'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
